import type { ApiErrorResponse } from '@/api/auth/auth.types'

/** Erro da API — compatível com `instanceof Error`. */
export class ApiError extends Error {
  readonly statusCode: number
  readonly error: string
  readonly code?: string

  constructor(response: ApiErrorResponse & { code?: string }) {
    super(response.message)
    this.name = 'ApiError'
    this.statusCode = response.statusCode
    this.error = response.error ?? 'Internal Server Error'
    if (response.code) this.code = response.code
  }
}

function readIssueMessages(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  const parts: string[] = []
  for (const item of value) {
    if (typeof item === 'string' && item.trim()) {
      parts.push(item.trim())
      continue
    }
    if (item && typeof item === 'object' && 'message' in item) {
      const message = (item as { message?: unknown }).message
      if (typeof message === 'string' && message.trim()) {
        const path = (item as { path?: unknown }).path
        const pathLabel =
          Array.isArray(path) && path.length > 0 ? `${path.join('.')}: ` : ''
        parts.push(`${pathLabel}${message.trim()}`)
      }
    }
  }
  return parts
}

/** Resume dezenas de paths Zod iguais em uma mensagem legível para toast. */
function summarizeIssueMessages(parts: string[]): string {
  if (parts.length === 0) return ''
  if (parts.length <= 3) return parts.join('. ')

  const grouped = new Map<string, number>()
  for (const part of parts) {
    // Primeiro ": " separa path do message (Zod 4: "Invalid input: expected…").
    const colonIdx = part.indexOf(': ')
    const path = colonIdx >= 0 ? part.slice(0, colonIdx) : part
    const message = colonIdx >= 0 ? part.slice(colonIdx + 2) : part
    const normalizedPath = path.replace(/\.\d+/g, '[]')
    const key = `${normalizedPath}: ${message}`
    grouped.set(key, (grouped.get(key) ?? 0) + 1)
  }

  const summaries = Array.from(grouped.entries()).map(([key, count]) =>
    count > 1 ? `${key} (${count}x)` : key,
  )

  if (summaries.length <= 4) return summaries.join('. ')

  const shown = summaries.slice(0, 3).join('. ')
  return `${shown}. … e mais ${summaries.length - 3} tipo(s) de erro (${parts.length} no total).`
}

function isGenericValidationMessage(message: string): boolean {
  return /^validation failed$/i.test(message.trim())
}

function isInternalServerMessage(message: string): boolean {
  return /^internal server error$/i.test(message.trim())
}

function readMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) {
    const parts = readIssueMessages(value)
    if (parts.length > 0) return summarizeIssueMessages(parts)
  }
  return fallback
}

function readMessageFromApiBody(
  body: Record<string, unknown>,
  fallback: string,
): string {
  const nestedError =
    typeof body.error === 'object' && body.error !== null
      ? (body.error as Record<string, unknown>)
      : null
  const fromNestedErrorMessage = nestedError
    ? readMessage(nestedError.message, '')
    : ''

  const fromMessage = readMessage(body.message, '')

  const fromErrors = readIssueMessages(body.errors)
  const fromIssues = readIssueMessages(body.issues)
  const details = fromErrors.length > 0 ? fromErrors : fromIssues

  if (fromMessage && !isGenericValidationMessage(fromMessage)) {
    return fromMessage
  }
  if (fromNestedErrorMessage && !isGenericValidationMessage(fromNestedErrorMessage)) {
    return fromNestedErrorMessage
  }
  if (details.length > 0) return summarizeIssueMessages(details)
  if (fromMessage) return fromMessage
  if (fromNestedErrorMessage) return fromNestedErrorMessage

  return fallback
}

/** Mantém a mensagem do backend; só substitui erros técnicos sem conteúdo útil. */
function resolveApiErrorMessage(message: string, fallback: string): string {
  const trimmed = message.trim()
  if (!trimmed || isInternalServerMessage(trimmed)) return fallback
  return trimmed
}

function unwrapApiErrorData(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed) return null
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return unwrapApiErrorData(JSON.parse(trimmed))
      } catch {
        return { message: trimmed }
      }
    }
    return { message: trimmed }
  }

  if (typeof data === 'object' && data !== null) {
    return data as Record<string, unknown>
  }

  return null
}

function readApiClientEnvelope(
  record: Record<string, unknown>,
  fallbackMessage: string,
): ApiErrorResponse | null {
  const topLevelMessage = readMessage(record.message, '')
  const nestedData = unwrapApiErrorData(record.data)

  if (nestedData) {
    const nestedMessage = readMessageFromApiBody(nestedData, fallbackMessage)
    const message =
      topLevelMessage && !isGenericValidationMessage(topLevelMessage)
        ? topLevelMessage
        : nestedMessage

    return {
      message: resolveApiErrorMessage(message, fallbackMessage),
      statusCode:
        typeof nestedData.statusCode === 'number'
          ? nestedData.statusCode
          : typeof record.status === 'number'
            ? record.status
            : 500,
      error:
        typeof nestedData.error === 'string'
          ? nestedData.error
          : 'Internal Server Error',
    }
  }

  if (
    topLevelMessage &&
    (typeof record.status === 'number' || typeof record.statusCode === 'number')
  ) {
    return {
      message: resolveApiErrorMessage(topLevelMessage, fallbackMessage),
      statusCode:
        typeof record.statusCode === 'number'
          ? record.statusCode
          : typeof record.status === 'number'
            ? record.status
            : 500,
      error:
        typeof record.error === 'string'
          ? record.error
          : 'Internal Server Error',
    }
  }

  if (
    typeof record.message !== 'undefined' ||
    typeof record.statusCode === 'number' ||
    typeof record.status === 'number'
  ) {
    return {
      message: resolveApiErrorMessage(
        readMessage(record.message, fallbackMessage),
        fallbackMessage,
      ),
      statusCode:
        typeof record.statusCode === 'number'
          ? record.statusCode
          : typeof record.status === 'number'
            ? record.status
            : 500,
      error:
        typeof record.error === 'string'
          ? record.error
          : 'Internal Server Error',
    }
  }

  return null
}

export function normalizeApiError(
  error: unknown,
  fallbackMessage: string,
): ApiErrorResponse {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      statusCode: error.statusCode,
      error: error.error,
    }
  }

  if (typeof error === 'string' && error.trim()) {
    return {
      message: resolveApiErrorMessage(error, fallbackMessage),
      statusCode: 500,
      error: 'Internal Server Error',
    }
  }

  if (typeof error === 'object' && error !== null) {
    const issues = (error as { issues?: unknown }).issues
    if (Array.isArray(issues)) {
      const fromIssues = readIssueMessages(issues)
      if (fromIssues.length > 0) {
        return {
          message: summarizeIssueMessages(fromIssues),
          statusCode: 400,
          error: 'Validation Error',
        }
      }
    }

    const record = error as Record<string, unknown>
    const fromEnvelope = readApiClientEnvelope(record, fallbackMessage)
    if (fromEnvelope) return fromEnvelope
  }

  if (error instanceof Error && error.message.trim()) {
    return {
      message: resolveApiErrorMessage(error.message, fallbackMessage),
      statusCode: 500,
      error: error.name || 'Error',
    }
  }

  return {
    message: fallbackMessage,
    statusCode: 500,
    error: 'Internal Server Error',
  }
}

/** Indica falha de validação do schema de resposta no cliente (Zod). */
export function isClientResponseSchemaError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'issues' in error) {
    return Array.isArray((error as { issues?: unknown }).issues);
  }

  const message = getApiErrorMessage(error, '');
  return message.startsWith('[') && message.includes('invalid_type');
}

/** Extrai a mensagem da API para exibir ao usuário (sem reescrever o backend). */
export function getApiErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  return normalizeApiError(error, fallbackMessage).message
}

/** Propaga erro da API/Zod preservando a mensagem retornada pelo backend. */
export function rethrowApiError(
  error: unknown,
  fallbackMessage: string,
): never {
  throw new ApiError(normalizeApiError(error, fallbackMessage))
}
